import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Counter } from '@adornis/counter/counter.js';
import { Payment } from '@adornis/payments/db/payments.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { getPluginForScope } from '../plugins/plugin.js';
import { Participation } from './Participation.js';
import { type User } from './user.js';

@Entity()
export class BHOrderNr extends AdornisEntity {
  static override _class = 'BHOrderNr';

  @validate(nonOptional())
  @Field(type => Int)
  year!: number;

  @validate(nonOptional())
  @Field(type => Int)
  nr!: number;

  static async create(institutionID: string, ...other: any) {
    const year = DateTime.now().year;
    const nr = await Counter.getIncrementingNumber(institutionID + '::orderCounter::' + year)();
    return new BHOrderNr({ year, nr });
  }

  getOrderNr() {
    const padNr = this.nr.toString().padStart(6, '0');
    return `${this.year}/${padNr}`;
  }
}

@Entity()
export class BHPayment extends Payment {
  static override _class = 'BHPayment';
  static override _collectionName = 'payments';

  @validate(nonOptional())
  @Field(type => [String])
  participationIDs!: string[];

  @DataLoadedField(type => [Participation], ({ participationIDs }) => participationIDs)
  participations!: Participation[];

  @Field(type => BHOrderNr)
  orderNr!: BHOrderNr;

  // Note: currently only used for sd-plugin-invoices
  @Field(type => Int)
  discount?: number;

  static override async beforeCreate(entity: BHPayment) {
    const participationID = entity.participationIDs[0];
    if (!participationID) throw new Error('no participations available');
    const participation = await getByID(
      Participation,
      participationID,
    )({ _id: 1, product: { _id: 1, institutionID: 1 } });
    if (!participation) throw new Error('participation not found');
    const product = participation.product;
    if (!product) throw new Error('product not found');
    const institutionID = product.institutionID;
    if (!(entity.orderNr as any)) entity.orderNr = await BHOrderNr.create(institutionID);

    if (!(entity.createdAt as any)) entity.createdAt = DateTime.now();
  }

  static override async afterUpdate(oldPayment: BHPayment, newPayment: BHPayment, _result: any) {
    const resolvedNewPayment = await getByID(BHPayment, newPayment._id)(BHPayment.allFields);
    if (!resolvedNewPayment) throw new Error('somthing went wrong');
    const participationID = resolvedNewPayment.participationIDs[0];
    if (!participationID) return;
    const participation = await getByID(Participation, participationID)({ product: { institutionID: 1 } });
    if (!participation) return;
    const institutionID = participation.product?.institutionID;
    if (!institutionID) return;
    const paymentPlugin = getPluginForScope('payment', institutionID);
    await paymentPlugin.onPaymentUpdate?.(institutionID, oldPayment, resolvedNewPayment);
  }

  @Query(type => BHPayment)
  static getPaidPaymentForRegisterData(@Arg('registerDataID', type => String) registerDataID: string) {
    return async (gqlFields: BaseQLSelectionSet<BHPayment>) => {
      const collection = await getCollection<BHPayment>(this._class);
      const result = await collection.findOne<BHPayment>({ status: 'paid', registerDataID });
      return result;
    };
  }

  @Query(type => [String])
  static getUserProductsForWhichAPaymentExists() {
    return async () => {
      // get all user owned payments
      const paymentCollection = await getCollection(BHPayment._class);
      const payments = (await paymentCollection.find().toArray()) as BHPayment[];

      return payments.reduce<string[]>((acc, payment) => {
        const products = payment.products.map(cp => cp.product._id);
        return [...acc, ...products];
      }, []);
    };
  }

  static async getAllowedPayments() {
    const { User } = await import('./user');
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];

    const userIDs = await User.getAllowedUsers();

    const paymentIDs: string[] = [];

    // ADMIN
    const result = await (
      await getRawCollection(Payment._collectionName)
    )
      .aggregate<{ _id: string }>([
        {
          $match: {
            'userReference.userID': {
              $in: userIDs,
            },
          },
        },
        {
          $project: {
            _id: 1,
          },
        },
      ])
      .toArray();

    paymentIDs.push(...result.map(res => res._id));

    return paymentIDs;
  }
}
